import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Title } from "../../../../themes/Default/components/Title";
import { Main } from "../../../../themes/Default/components/Main";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { BodyAttendance } from "../../../../themes/Default/components/List/components/Body";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import DatePicker from "../../../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { Wizard } from "../../../../themes/Default/components/Wizard";
import { Step } from "../../../../themes/Default/components/Wizard/Step";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const ProgressTests = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { groupId } = useParams();
    const {groupProgressTestsId} = useParams();
    const { useListAttendancesMutation, useViewGroupMutation, useAddProgressTestMutation, useProgressTestListMutation, useUpdateProgressTestMutation} = groupsManagement;
    const [attendancesList, { isLoading }] = useListAttendancesMutation();
    const [progressTestList] = useProgressTestListMutation();
    const [addProgressTest] = useAddProgressTestMutation();
    const [editProgressTest] = useUpdateProgressTestMutation();
    const [students, setStudents] = useState();
    const [groupProgressTests, setGroupProgressTests] = useState();
    const [formErrors, setFormErrors] = useState([]);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);

    const [groupName, setGroupName] = useState("");
    const [viewGroup] = useViewGroupMutation();
    const [rowData, setRowData] = useState({});

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []),
        { title: t('groups.progressTest'),isActive: true },
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []), 
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const gotoEditTest = () => {
        navigate("/groups/" + groupId + "/tests/edit/" + groupProgressTestsId);
    }

    const handleChange = (studentId, field, value) => {
        setRowData((prev) => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value
            }
        }));
    };

    const onSaved = (values, studentId) => {
        const body = { ...values, studentId };
        addProgressTest({body, groupProgressTestsId}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result?.error?.data?.error?.responseMessage);
            } else {
                toast.info(result?.data?.info?.responseMessage);
                loadGroupProgressTests();
            }
        });
    };

    const onEdit = (values, studentId, testId) => {
        const body = { ...values, studentId };
        editProgressTest({body, testId}).then((result) => {
            if (result?.error) {
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadGroupProgressTests();
            }
        });
    };

    const loadGroupProgressTests = () => {
        progressTestList({groupProgressTestsId}).then((result) => {
            setGroupProgressTests(result.data);
        });
    }

    useEffect(() => {
        setStudents([]);
        setGroupProgressTests([]);
        setFormErrors([]);

        attendancesList({ groupId: groupId }).then((result) => {
            setStudents(result?.data?.students);
        });
        loadGroupProgressTests();
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
    }, [groupId]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/" + groupId + "/tests/edit/" + groupProgressTestsId} />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("Testovi")} isActive={true} />
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t('Tip testa')} isChecked={true} onClick={gotoEditTest}/>
                    <Step title={t('Rezultati')} isActive={true}/>
                </Wizard>
                <br/>
                <br/>
                <br/>
                <List>
                    <ListHeader>                
                    </ListHeader>
                    <BodyAttendance>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.no")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.fullName")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("calendar.date")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.totalScore")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.totalMaximum")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.readingScore")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.readingMaximum")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.writingScore")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.writingMaximum")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.speakingScore")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.speakingMaximum")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.listeningScore")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.listeningMaximum")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                </TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {students?.length > 0 && (() => {
                            let activeIndex = 0;
                            return students.map((student) => {
                                    if (!student.isArchived) activeIndex++;
                                    const studentTests = groupProgressTests.find(testData => testData.studentId === student.id)?.tests || [];
                                    const studentTestDate = studentTests[0]?.date || '';
                                    const studentA = groupProgressTests.find(testData => testData.studentId === student.id) || [];
                                    const formatDateFromBackend = (dateString) => {
                                        if (!dateString) return '';
                                        const [year, month, day] = dateString.split('-');
                                        return `${day}.${month}.${year}`;
                                    };
                                    return (
                                        <TableRow key={student.id}>
                                            <TableDataCell>
                                                {!student.isArchived ? (
                                                    <Button 
                                                        icon="fa-regular fa-floppy-disk" 
                                                        onClick={() => {
                                                            if(studentTests[0]?.id){
                                                                onEdit(rowData[student.id], student.id, studentTests[0]?.id);
                                                            }else{
                                                                onSaved(rowData[student.id], student.id);
                                                            }
                                                            setRowData((prev) => {
                                                                const newData = { ...prev };
                                                                delete newData[student.id]; 
                                                                return newData;
                                                            });
                                                        }}
                                                    />
                                                    ) : null}
                                            </TableDataCell>
                                            <TableDataCell>{!student.isArchived ? String(activeIndex).padStart(2, "0") : ''}</TableDataCell>
                                            <TableDataCell>
                                                <span style={{ color: student.isArchived ? '#ff0000' : '' }}>
                                                    {student.name}
                                                </span>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field isFullWidth={true}>
                                                    {student.isArchived ? (
                                                    <Input
                                                        disabled
                                                        widthSize="200px"
                                                        name="date"
                                                        value={studentTestDate ? formatDateFromBackend(studentTestDate) : ''}
                                                        textAlign="center"
                                                    />
                                                    ) : (
                                                    <DatePicker
                                                        widthSize="200px"
                                                        name="date"
                                                        value={studentTestDate ? formatDateFromBackend(studentTestDate) : ''}
                                                        onChange={(value) => handleChange(student.id, "date", value.target.value)}
                                                        error={studentA.studentId !== student.id ? formErrors?.date : null}
                                                    />
                                                    )}
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="totalPointsScore"
                                                        onChange={(e) => handleChange(student.id, "totalPointsScore", e.target.value)}
                                                        value={rowData[student.id]?.totalPointsScore || studentTests[0]?.totalPointsScore}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="totalPointsMax"
                                                        onChange={(e) => handleChange(student.id, "totalPointsMax", e.target.value)}
                                                        value={rowData[student.id]?.totalPointsMax || studentTests[0]?.totalPointsMax}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="readingScore"
                                                        onChange={(e) => handleChange(student.id, "readingScore", e.target.value)}
                                                        value={rowData[student.id]?.readingScore || studentTests[0]?.readingScore}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="readingMax"
                                                        onChange={(e) => handleChange(student.id, "readingMax", e.target.value)}
                                                        value={rowData[student.id]?.readingMax || studentTests[0]?.readingMax}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="writingScore"
                                                        onChange={(e) => handleChange(student.id, "writingScore", e.target.value)}
                                                        value={rowData[student.id]?.writingScore || studentTests[0]?.writingScore}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="writingMax"
                                                        onChange={(e) => handleChange(student.id, "writingMax", e.target.value)}
                                                        value={rowData[student.id]?.writingMax || studentTests[0]?.writingMax}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="speakingScore"
                                                        onChange={(e) => handleChange(student.id, "speakingScore", e.target.value)}
                                                        value={rowData[student.id]?.speakingScore || studentTests[0]?.speakingScore}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="speakingMax"
                                                        onChange={(e) => handleChange(student.id, "speakingMax", e.target.value)}
                                                        value={rowData[student.id]?.speakingMax || studentTests[0]?.speakingMax}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="listeningScore"
                                                        onChange={(e) => handleChange(student.id, "listeningScore", e.target.value)}
                                                        value={rowData[student.id]?.listeningScore || studentTests[0]?.listeningScore}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <Field>
                                                    <Input
                                                        disabled={student.isArchived}
                                                        widthSize="80px"
                                                        alignSelf="center"
                                                        textAlign="center"
                                                        name="listeningMax"
                                                        onChange={(e) => handleChange(student.id, "listeningMax", e.target.value)}
                                                        value={rowData[student.id]?.listeningMax || studentTests[0]?.listeningMax}
                                                    />
                                                </Field>
                                            </TableDataCell>
                                            <TableDataCell></TableDataCell>
                                        </TableRow>
                                    );
                                });
                            })()}
                        </TableBody>
                    </BodyAttendance>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default ProgressTests;
